import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, qs } from '../api/client'
import type { BatchItem, BatchKind, BatchView, Paged, UndoResult } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { useOperation } from '../hooks/useOperation'
import { useAuth } from '../auth'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Badge, CopyListDialog, Empty, Modal, Notice, Pager, batchTone, fmtTime, statusTone } from '../components/ui'

const KINDS: { value: BatchKind | ''; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'INTAKE', label: '入库' },
  { value: 'OUTBOUND', label: '出库' },
  { value: 'RESTORE', label: '恢复' },
]

const KIND_LABEL: Record<string, string> = { INTAKE: '入库', OUTBOUND: '出库', RESTORE: '恢复' }

export function Batches() {
  const [params, setParams] = useSearchParams()
  const { user } = useAuth()
  const kind = params.get('kind') ?? ''
  const page = Number(params.get('page') ?? '1')
  const size = 25
  const highlight = Number(params.get('highlight') ?? '0')
  const [open, setOpen] = useState<number | null>(highlight || null)
  const [confirmUndo, setConfirmUndo] = useState<BatchView | null>(null)
  const [done, setDone] = useState<{ code: string; kind: BatchKind; domains: string[] } | null>(null)
  const op = useOperation<UndoResult>('undo')

  const { data, error, loading, reload } = useAsync(
    () => api.get<Paged<BatchView>>(`/api/batches${qs({ kind, page, size })}`),
    [kind, page],
  )
  const rows = data?.items ?? []

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    if (k !== 'page') next.set('page', '1')
    setParams(next, { replace: true })
  }

  async function undo(b: BatchView) {
    const path = b.kind === 'INTAKE' ? '/api/batches/latest-intake/undo' : '/api/batches/latest-outbound/undo'
    const res = await op.run((key) => api.post<UndoResult>(path, { confirm: true }, key))
    if (res) {
      setConfirmUndo(null)
      // 撤回到底改动了哪些域名，当时就说清楚 —— 只刷新列表的话，
      // 操作员还得自己切回库存页核对，等于没告诉他人成功了。
      setDone({ code: res.batchCode, kind: b.kind, domains: res.domains ?? [] })
      reload()
    }
  }

  return (
    <section>
      <header className="page-head compact">
        <h1>批次与履历</h1>
        <div className="row">
          <select aria-label="按批次类型筛选" value={kind} onChange={(e) => setParam('kind', e.target.value)}>
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <button type="button" onClick={reload} disabled={loading}>
            {loading ? '载入中…' : '刷新'}
          </button>
        </div>
      </header>

      <Notice error={error} />
      {!loading && rows.length === 0 && <Empty text="还没有任何批次。" />}

      {rows.length > 0 && (
        <div className="card table-card">
          <table className="table">
            <thead>
              <tr>
                <th>批次号</th>
                <th>类型</th>
                <th>状态</th>
                <th>数量</th>
                <th>区</th>
                <th>操作人</th>
                <th>时间</th>
                <th>备注</th>
                <th>可撤回</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className={b.id === highlight ? 'picked' : ''}>
                  <td className="mono">
                    <button type="button" className="link" onClick={() => setOpen(b.id)}>
                      {b.code}
                    </button>
                  </td>
                  <td>{KIND_LABEL[b.kind] ?? b.kind}</td>
                  <td>
                    <Badge tone={batchTone(b.status)}>{b.status}</Badge>
                  </td>
                  <td>{b.quantity}</td>
                  <td>{b.zone ? (b.zone === 'ZONE_A' ? 'A' : 'S') : '-'}</td>
                  <td>{b.actorName || '-'}</td>
                  <td className="muted">{fmtTime(b.committedAt ?? b.createdAt)}</td>
                  <td className="muted">{b.remark || b.undoReason || '-'}</td>
                  <td>
                    {b.undoable ? <Badge tone="ok">可撤回</Badge> : <span className="muted">{b.undoNote}</span>}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="danger link"
                      disabled={!b.undoable || !user?.canWrite}
                      onClick={() => {
                        op.newIntent()
                        setConfirmUndo(b)
                      }}
                    >
                      整批撤回
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} size={size} total={data?.total ?? 0} onPage={(p) => setParam('page', String(p))} />
        </div>
      )}

      {open && <BatchDetail id={open} onClose={() => setOpen(null)} />}

      {confirmUndo && (
        <ConfirmDialog
          title={confirmUndo.kind === 'INTAKE' ? '撤回整批入库' : '撤回整批出库'}
          description={
            confirmUndo.kind === 'INTAKE'
              ? `将把批次 ${confirmUndo.code} 入库的 ${confirmUndo.quantity} 个域名标记为已撤回，它们会从库存和统计里消失。入库撤回没有反向操作，等于把这批货从库里拿走，所以要打字确认。`
              : `将把批次 ${confirmUndo.code} 出库的 ${confirmUndo.quantity} 个域名全部退回在库，卖出履历保留不删。撤回是一个事务：只要有一个域名在出库后被别人改过状态，整批拒绝、一个都不动。`
          }
          expectText={confirmUndo.kind === 'INTAKE' ? '撤回' : undefined}
          confirmLabel="撤回这批"
          busy={op.busy}
          error={op.error}
          onConfirm={() => void undo(confirmUndo)}
          onClose={() => {
            setConfirmUndo(null)
            op.newIntent()
          }}
        />
      )}

      {done && (
        <CopyListDialog
          title={done.kind === 'INTAKE' ? `批次 ${done.code} 已撤回入库` : `批次 ${done.code} 已恢复为在库`}
          items={done.domains}
          note={
            done.kind === 'INTAKE'
              ? '这批域名已标记为已撤回，不再出现在库存与统计里；一行一个，需要重新入库就直接粘到入库页。'
              : '这些域名现在已经回到在库，域&Domain总库可以直接勾选；一行一个，可以发给买家或留底。'
          }
          onClose={() => setDone(null)}
        />
      )}
    </section>
  )
}

function BatchDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const { data, error, loading } = useAsync(
    () => api.get<{ batch: BatchView; items: BatchItem[] }>(`/api/batches/${id}`),
    [id],
  )
  const items = data?.items ?? []
  const undone = data?.batch.status === 'UNDONE'
  const backInStock = items.filter((it) => it.currentStatus === 'IN_STOCK' && it.status !== 'IN_STOCK').length

  return (
    <Modal title={data ? `批次 ${data.batch.code}` : `批次 #${id}`} onClose={onClose} width={760}>
      {loading && <p className="muted">载入中…</p>}
      <Notice error={error} />
      {data && (
        <>
          <div className="row wrap counts">
            <Badge tone={batchTone(data.batch.status)}>{data.batch.status}</Badge>
            <Badge>{KIND_LABEL[data.batch.kind] ?? data.batch.kind}</Badge>
            <Badge>共 {items.length} 个域名</Badge>
            {data.batch.undoable ? <Badge tone="ok">可整批撤回</Badge> : <Badge tone="muted">{data.batch.undoNote}</Badge>}
          </div>
          {undone && (
            <div className="notice info" role="status">
              本批已于 {fmtTime(data.batch.undoneAt)} 撤回
              {data.batch.kind === 'INTAKE'
                ? `，这 ${items.length} 个域名现在标记为已撤回，不在库存与统计里。`
                : `，${backInStock} 个域名已经回到在库，域&Domain总库可以直接勾选。`}
              「批次当时」是这一批写进去的历史，永不改写；「现在的状态」才是此刻的库存。
            </div>
          )}
          <p className="muted">
            操作人 {data.batch.actorName || '-'} · 创建 {fmtTime(data.batch.createdAt)} · 提交{' '}
            {fmtTime(data.batch.committedAt)} · 请求 {data.batch.requestId || '-'}
          </p>
          {data.batch.remark && <p>备注：{data.batch.remark}</p>}
          <div className="scroll-x">
            <table className="table compact">
              <thead>
                <tr>
                  <th>域名</th>
                  <th>批次当时</th>
                  <th>现在的状态</th>
                  <th>入库批次</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.domainId}>
                    <td className="mono">{it.domain}</td>
                    <td>
                      <Badge tone={statusTone(it.status)}>{it.status}</Badge> <span className="muted">v{it.version}</span>
                    </td>
                    <td>
                      <Badge tone={statusTone(it.currentStatus)}>{it.currentStatus}</Badge>{' '}
                      <span className="muted">v{it.currentVersion}</span>
                    </td>
                    <td>#{it.intakeBatchId}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  )
}
